import { Fragment, type ReactNode } from 'react'
import { clsx } from 'clsx'
import { Check, Pause, Play } from 'lucide-react'

import { type DiffTurn, diffTurns, type Focus, focusOf, nameDiffers, splitWords } from './transcriptDiff'

import styles from './ModelComparison.module.scss'

import type { TurnPreferenceChoice, TurnPreferenceUnit } from '@/api/internal'
import { type SpeakerStyleMap } from '@/pages/app/AttributionViewer/components/colors'
import { formatMs } from '@/pages/app/AttributionViewer/components/Swimlane'

const NO_NAME_COLOR = '#898781'

const formatDuration = (ms: number): string => {
  const seconds = Math.round(ms / 1000)
  return seconds < 60 ? `${seconds} s` : formatMs(ms)
}

type SideProps = {
  choice: TurnPreferenceChoice
  turns: DiffTurn[]
  others: DiffTurn[]
  unmatched: boolean[][]
  picked: boolean
  anyPicked: boolean
  readOnly: boolean
  clipLabel: string
  focus: Focus
  speakerStyles: SpeakerStyleMap
  onPick: () => void
}

const SideCard = ({
  choice,
  turns,
  others,
  unmatched,
  picked,
  anyPicked,
  readOnly,
  clipLabel,
  focus,
  speakerStyles,
  onPick,
}: SideProps) => {
  const letter = choice.toUpperCase()
  const words = focus === 'words' ? turns.flatMap((turn) => splitWords(turn.text)) : []
  const flags = focus === 'words' ? unmatched.flat() : []
  const body: ReactNode = (
    <>
      <span className={styles.sideLabel}>Side {letter}</span>
      <span className={styles.turns}>
        {turns.length === 0 && <span className={clsx(styles.text, styles.silent)}>(nothing transcribed here)</span>}
        {/* Word clips render as one block: names, colors and even turn breaks would hint at who spoke. */}
        {focus === 'words' && turns.length > 0 && (
          <span className={styles.text}>
            {words.map((word, wordIndex) => (
              <Fragment key={wordIndex}>
                {wordIndex > 0 && ' '}
                {flags[wordIndex] ? <mark className={styles.diffWord}>{word}</mark> : word}
              </Fragment>
            ))}
          </span>
        )}
        {focus === 'speakers' &&
          turns.map((turn, turnIndex) => (
            <span key={`${turn.start_ms}-${turnIndex}`} className={styles.turn}>
              <span className={clsx(styles.who, nameDiffers(turn, others) && styles.whoDiff)}>
                <span
                  className={styles.dot}
                  style={{ background: speakerStyles.get(turn.name)?.color ?? NO_NAME_COLOR }}
                  aria-hidden="true"
                />
                <span className={styles.name}>{turn.name || 'Unknown'}</span>
              </span>
              <span className={styles.text}>{turn.text}</span>
            </span>
          ))}
      </span>
      {readOnly ? (
        picked ? (
          <span className={styles.yours}>
            <Check size={13} aria-hidden="true" /> Your pick
          </span>
        ) : (
          !anyPicked && <span className={styles.prefHint}>No preference</span>
        )
      ) : (
        <span className={styles.pref}>
          {picked ? (
            <>
              <span className={styles.prefMark}>
                <Check size={13} aria-hidden="true" /> Preferred
              </span>
              <span className={styles.prefHint}>Click again to clear</span>
            </>
          ) : (
            <span className={styles.prefHint}>Click to prefer {letter}</span>
          )}
        </span>
      )}
    </>
  )

  if (readOnly) return <div className={clsx(styles.side, picked && styles.sidePicked)}>{body}</div>
  return (
    <button
      type="button"
      className={clsx(styles.side, styles.sideButton, picked && styles.sidePicked)}
      aria-pressed={picked}
      aria-label={`${clipLabel}, side ${letter}`}
      onClick={onPick}
    >
      {body}
    </button>
  )
}

type UnitProps = {
  unit: TurnPreferenceUnit
  index: number
  pick: TurnPreferenceChoice | undefined
  readOnly: boolean
  speakerStyles: SpeakerStyleMap
  playing: boolean
  playDisabled?: boolean
  onPick: (choice: TurnPreferenceChoice) => void
  onTogglePlay: () => void
  // Playhead position within the unit, 0-1; only meaningful while playing.
  progress: number
}

const ModelComparisonUnit = ({
  unit,
  index,
  pick,
  readOnly,
  speakerStyles,
  playing,
  playDisabled = false,
  onPick,
  onTogglePlay,
  progress,
}: UnitProps) => {
  const turnsA = unit.sides.a.turns
  const turnsB = unit.sides.b.turns
  const focus = focusOf(unit)
  // Speaker clips are judged on attribution alone, so word differences are never highlighted there.
  const diff = focus === 'words' ? diffTurns(turnsA, turnsB) : { a: [], b: [] }
  const range = `${formatMs(unit.start_ms)} – ${formatMs(unit.end_ms)}`
  const clipLabel = `Clip ${index + 1}`

  return (
    <section className={styles.unit} aria-label={clipLabel}>
      <div className={styles.unitHead}>
        <h4 className={styles.unitTitle}>
          {clipLabel} · {range}
        </h4>
        <span className={styles.focus}>{focus === 'speakers' ? 'Speakers' : 'Words'}</span>
        {pick && !readOnly && <span className={styles.chosen}>Chosen</span>}
      </div>
      {playing && (
        <div className={styles.playing}>
          <div className={styles.bar}>
            <i style={{ width: `${Math.round(Math.min(1, Math.max(0, progress)) * 100)}%` }} />
          </div>
          <span>Playing {range} · whole clip, both sides</span>
        </div>
      )}
      <div className={styles.sides}>
        <div className={styles.playCol}>
          <button
            type="button"
            className={clsx(styles.play, playing && styles.playActive)}
            aria-pressed={playing}
            aria-label={`${playing ? 'Pause' : 'Play'} ${clipLabel}`}
            disabled={playDisabled && !playing}
            onClick={onTogglePlay}
          >
            {playing ? <Pause size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
            {playing ? 'Pause' : 'Play'}
          </button>
          <span className={styles.range}>{formatDuration(unit.end_ms - unit.start_ms)}</span>
        </div>
        <SideCard
          choice="a"
          turns={turnsA}
          others={turnsB}
          unmatched={diff.a}
          picked={pick === 'a'}
          anyPicked={pick !== undefined}
          readOnly={readOnly}
          clipLabel={clipLabel}
          focus={focus}
          speakerStyles={speakerStyles}
          onPick={() => onPick('a')}
        />
        <SideCard
          choice="b"
          turns={turnsB}
          others={turnsA}
          unmatched={diff.b}
          picked={pick === 'b'}
          anyPicked={pick !== undefined}
          readOnly={readOnly}
          clipLabel={clipLabel}
          focus={focus}
          speakerStyles={speakerStyles}
          onPick={() => onPick('b')}
        />
      </div>
    </section>
  )
}

export default ModelComparisonUnit
